import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FiTrendingUp } from "react-icons/fi";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";
import paths from "../../../utils/constants";
import imagePaths from "../../../assets/imagePaths";
import api from "../../../api/api";
import { logMessage } from "../../../utils/helpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { cn } from "../../../lib/utils";
import { getPointsProgressDisplay } from "../../../utils/gamificationProgress";
import { AnimatePresence, motion } from "framer-motion";
import { DiamondIcon } from "./Icons";

const PURPLE = "#552F83";
const PURPLE_SOFT = "#FAF5FF";
const PAGE_BG = "#f1ecff";

const waysAccordionTransition = {
  duration: 0.28,
  ease: [0.25, 0.1, 0.25, 1],
};

const WEEKLY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHLY_LABELS = ["01-05", "06-10", "11-15", "16-20", "21-25", "26-31"];
const YEARLY_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** API accepts month names (e.g. "august") or 0–11; mobile-friendly. */
function monthIndexToApiMonthKey(month0to11) {
  return new Date(2000, Number(month0to11), 1)
    .toLocaleString("en-US", { month: "long" })
    .toLowerCase();
}

const emptyWeekly = () => WEEKLY_LABELS.map((label) => ({ label, points: 0 }));
const emptyMonthly = () => MONTHLY_LABELS.map((label) => ({ label, points: 0 }));
const emptyYearly = () => YEARLY_LABELS.map((label) => ({ label, points: 0 }));

/** Section chrome + task key order; copy from API `gamification_config` (description + value). */
const WAYS_SECTION_LAYOUT = [
  {
    id: "painting_sessions",
    title: "Painting Sessions",
    subtitle: "Earn Points through active painting time.",
    icon: "clock",
    keys: ["xp_session_start", "xp_time_block_30min", "xp_session_end"],
    good_to_know: [
      "Points is counted only while the timer runs.",
      "Paused time does not earn Points.",
      "A daily session Points limit may apply.",
    ],
  },
  {
    id: "project_milestones",
    title: "Project Milestones",
    subtitle: "Points awarded as your project progresses.",
    icon: "clipboard",
    keys: ["xp_project_create", "xp_milestone_25", "xp_milestone_50", "xp_milestone_75", "xp_milestone_100"],
    good_to_know: [
      "Milestones are awarded once per project.",
      "Completion requires verified confirmation.",
    ],
  },
  {
    id: "consistency_streaks",
    title: "Consistency & Streaks",
    subtitle: "Earn Points for painting regularly.",
    icon: "flame",
    keys: ["xp_daily_activity", "xp_streak_3_day", "xp_streak_7_day", "xp_streak_30_day"],
    good_to_know: [
      "One grace day is allowed every 7 days.",
      "Grace day is applied automatically if you miss one day.",
    ],
  },
];

/** Short labels when API `description` is missing (older DB rows, partial payloads). */
const TASK_LABEL_BY_KEY = {
  xp_session_start: "Start painting with the timer",
  xp_time_block_30min: "Every 30 minutes of active painting",
  xp_session_end: "End session normally",
  xp_project_create: "Create a new project",
  xp_milestone_25: "25% complete",
  xp_milestone_50: "50% complete",
  xp_milestone_75: "75% complete",
  xp_milestone_100: "100% complete",
  xp_daily_activity: "Daily painting activity",
  xp_streak_3_day: "3-day streak",
  xp_streak_7_day: "7-day streak",
  xp_streak_30_day: "30-day streak",
};

function pickConfigDescription(row, key) {
  if (!row || typeof row !== "object") return "";
  const raw =
    row.description ??
    row.desc ??
    row.Detail ??
    row.DESCRIPTION ??
    "";
  const s = String(raw).trim();
  if (s) return s;
  return TASK_LABEL_BY_KEY[key] || key;
}

function buildWaysSectionsFromConfig(configRows) {
  const rows = Array.isArray(configRows) ? configRows : [];
  const byKey = Object.fromEntries(
    rows.filter((r) => r && r.key != null).map((r) => [String(r.key), r])
  );
  return WAYS_SECTION_LAYOUT.map(({ keys, ...meta }) => ({
    ...meta,
    tasks: keys
      .map((k) => {
        const row = byKey[k];
        if (!row) return null;
        const description = pickConfigDescription(row, k);
        const value = row.value;
        const points = value === null || value === undefined || value === "" ? null : Number(value);
        if (k === "xp_streak_30_day") {
          return {
            key: k,
            description,
            points: null,
            points_label: "Badge + Points bonus",
          };
        }
        return {
          key: k,
          description,
          points: Number.isFinite(points) ? points : null,
        };
      })
      .filter(Boolean),
  }));
}

function isApiSuccess(res) {
  if (res == null) return false;
  const s = res.status;
  return s === 1 || s === "1" || Number(s) === 1;
}

function normalizeDashboardPayload(raw, localYear, localMonth) {
  if (!raw || typeof raw !== "object") return null;

  const popTier = raw.current_tier;
  const tierFromRoot =
    popTier && typeof popTier === "object" && !Array.isArray(popTier) ? popTier : {};

  let summary = raw.summary;
  if (!summary || typeof summary !== "object") {
    summary = {
      total_xp: Number(raw.total_xp ?? 0),
      current_level: Number(raw.current_level ?? 1),
      level_title: raw.level_title,
      current_xp:
        raw.current_xp != null && raw.current_xp !== "" ? Number(raw.current_xp) : null,
      xp_required_for_completion:
        raw.xp_required_for_completion != null && raw.xp_required_for_completion !== ""
          ? Number(raw.xp_required_for_completion)
          : null,
      points_away_text: raw.points_away_text,
      current_tier: {
        name: tierFromRoot.name || "",
        key: String(tierFromRoot.key || "bronze").toLowerCase(),
        icon: tierFromRoot.icon || "",
      },
    };
  } else {
    summary = { ...summary };
    if (!summary.current_tier || typeof summary.current_tier !== "object") {
      summary.current_tier = {
        name: tierFromRoot.name || "",
        key: String(tierFromRoot.key || "bronze").toLowerCase(),
        icon: tierFromRoot.icon || "",
      };
    }
    if (!summary.current_tier.name && tierFromRoot.name) {
      summary.current_tier = {
        name: tierFromRoot.name,
        key: String(tierFromRoot.key || summary.current_tier.key || "bronze").toLowerCase(),
        icon: tierFromRoot.icon || summary.current_tier.icon || "",
      };
    }
  }

  const c = raw.chart || {};
  const chart = {
    weekly: {
      week_offset: c.weekly?.week_offset ?? 0,
      week_start: c.weekly?.week_start,
      week_end: c.weekly?.week_end,
      range_label: c.weekly?.range_label || "",
      series:
        Array.isArray(c.weekly?.series) && c.weekly.series.length
          ? c.weekly.series
          : emptyWeekly(),
    },
    monthly: {
      year: c.monthly?.year ?? localYear,
      month_index: c.monthly?.month_index ?? localMonth,
      month_label:
        c.monthly?.month_label ||
        new Date(localYear, localMonth, 1).toLocaleString("en-US", {
          month: "long",
          year: "numeric",
        }),
      series:
        Array.isArray(c.monthly?.series) && c.monthly.series.length
          ? c.monthly.series
          : emptyMonthly(),
    },
    yearly: {
      year: c.yearly?.year ?? localYear,
      series:
        Array.isArray(c.yearly?.series) && c.yearly.series.length
          ? c.yearly.series
          : emptyYearly(),
    },
  };

  const gamificationConfig = Array.isArray(raw.gamification_config) ? raw.gamification_config : [];
  const ways_to_earn = buildWaysSectionsFromConfig(gamificationConfig);

  return { ...raw, summary, chart, gamification_config: gamificationConfig, ways_to_earn };
}

function mergeProgressIntoDashboard(normalized, progressPayload) {
  if (!normalized || !progressPayload || typeof progressPayload !== "object") {
    return normalized;
  }
  const tier = progressPayload.current_tier || {};
  const s = normalized.summary || {};
  return {
    ...normalized,
    summary: {
      ...s,
      total_xp: Number(
        progressPayload.total_xp != null ? progressPayload.total_xp : s.total_xp ?? 0
      ),
      current_level: Number(
        progressPayload.current_level != null
          ? progressPayload.current_level
          : s.current_level ?? 1
      ),
      level_title: progressPayload.level_title || s.level_title,
      current_xp:
        progressPayload.current_xp != null && progressPayload.current_xp !== ""
          ? Number(progressPayload.current_xp)
          : s.current_xp,
      xp_required_for_completion:
        progressPayload.xp_required_for_completion != null &&
        progressPayload.xp_required_for_completion !== ""
          ? Number(progressPayload.xp_required_for_completion)
          : s.xp_required_for_completion,
      points_away_text: progressPayload.points_away_text || s.points_away_text,
      current_tier: {
        name:
          tier.name != null && String(tier.name).length > 0
            ? tier.name
            : s.current_tier?.name || "",
        key: String(tier.key || s.current_tier?.key || "bronze").toLowerCase(),
        icon: tier.icon || s.current_tier?.icon || "",
      },
    },
  };
}

const PeriodPickerButton = React.forwardRef(({ value, onClick, className }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    className={cn(
      "inline-flex min-w-[10rem] max-w-[18rem] items-center justify-center gap-0.5 px-3 py-1.5 text-sm font-medium text-foreground rounded-md border-0 bg-transparent cursor-pointer hover:bg-muted",
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      className
    )}
  >
    <span className="min-w-0 truncate text-center text-[#1E232C]">{value}</span>
    <MdKeyboardArrowDown className="h-5 w-5 shrink-0 text-[#1E232C] ml-2" aria-hidden />
  </button>
));
PeriodPickerButton.displayName = "PeriodPickerButton";

const SectionIcon = ({ type }) => {
  if (type === "clock") {
    return (
      <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="42" height="42" rx="11" fill="#DBEAFE"/>
<path d="M21 11.5C19.6868 11.5 18.3864 11.7587 17.1732 12.2612C15.9599 12.7638 14.8575 13.5003 13.9289 14.4289C12.0536 16.3043 11 18.8478 11 21.5C11 24.1522 12.0536 26.6957 13.9289 28.5711C14.8575 29.4997 15.9599 30.2362 17.1732 30.7388C18.3864 31.2413 19.6868 31.5 21 31.5C23.6522 31.5 26.1957 30.4464 28.0711 28.5711C29.9464 26.6957 31 24.1522 31 21.5C31 20.1868 30.7413 18.8864 30.2388 17.6732C29.7362 16.4599 28.9997 15.3575 28.0711 14.4289C27.1425 13.5003 26.0401 12.7638 24.8268 12.2612C23.6136 11.7587 22.3132 11.5 21 11.5ZM25.2 25.7L20 22.5V16.5H21.5V21.7L26 24.4L25.2 25.7Z" fill="#2563EB"/>
</svg>

    );
  }
  if (type === "clipboard") {
    return (
      <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="42" height="42" rx="11" fill="#D1FAE5"/>
      <path d="M28 13.0002H23.82C23.6152 12.4156 23.2339 11.9092 22.7287 11.5508C22.2235 11.1925 21.6194 11 21 11C20.3806 11 19.7765 11.1925 19.2713 11.5508C18.7661 11.9092 18.3848 12.4156 18.18 13.0002H14C13.4701 13.0018 12.9623 13.213 12.5875 13.5877C12.2128 13.9625 12.0016 14.4703 12 15.0002V29.0002C12.0016 29.5301 12.2128 30.0379 12.5875 30.4127C12.9623 30.7874 13.4701 30.9986 14 31.0002H28C28.5299 30.9986 29.0377 30.7874 29.4125 30.4127C29.7872 30.0379 29.9984 29.5301 30 29.0002V15.0002C29.9984 14.4703 29.7872 13.9625 29.4125 13.5877C29.0377 13.213 28.5299 13.0018 28 13.0002ZM21 13.0002C21.1978 13.0002 21.3911 13.0589 21.5556 13.1687C21.72 13.2786 21.8482 13.4348 21.9239 13.6175C21.9996 13.8002 22.0194 14.0013 21.9808 14.1953C21.9422 14.3893 21.847 14.5675 21.7071 14.7073C21.5673 14.8472 21.3891 14.9424 21.1951 14.981C21.0011 15.0196 20.8 14.9998 20.6173 14.9241C20.4346 14.8484 20.2784 14.7202 20.1685 14.5558C20.0586 14.3913 20 14.198 20 14.0002C20.0008 13.7352 20.1064 13.4813 20.2938 13.294C20.4811 13.1066 20.735 13.001 21 13.0002ZM19 18.0002L21.79 20.7942L24.31 18.2742L23 17.0002H27V21.0002L25.724 19.6892L21.792 23.6242L19 20.8302L16.414 23.4142L15 22.0002L19 18.0002ZM28 28.0002H14V26.0002H28V28.0002Z" fill="#059669"/>
      </svg>
      
    );
  }
  return (
    <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="42" height="42" rx="11" fill="#FFF5D8"/>
    <path d="M21.832 31.3002C24.958 30.6742 29 28.4252 29 22.6102C29 17.3192 25.127 13.7952 22.342 12.1762C21.723 11.8162 21 12.2892 21 13.0042V14.8322C21 16.2742 20.394 18.9062 18.71 20.0012C17.85 20.5602 16.92 19.7232 16.816 18.7032L16.73 17.8652C16.63 16.8912 15.638 16.3002 14.86 16.8942C13.461 17.9592 12 19.8292 12 22.6092C12 29.7202 17.289 31.4992 19.933 31.4992C20.0877 31.4992 20.249 31.4942 20.417 31.4842C19.111 31.3732 17 30.5632 17 27.9432C17 25.8932 18.495 24.5082 19.631 23.8332C19.937 23.6532 20.294 23.8882 20.294 24.2432V24.8332C20.294 25.2832 20.469 25.9882 20.884 26.4702C21.354 27.0162 22.043 26.4442 22.098 25.7262C22.116 25.5002 22.344 25.3562 22.54 25.4702C23.181 25.8452 24 26.6452 24 27.9432C24 29.9912 22.871 30.9332 21.832 31.3002Z" fill="#B58F08"/>
    </svg>
    
  );
};

const CHART_HEIGHT = 300;

function buildYAxisScale(series) {
  const maxPoint = Math.max(0, ...((series || []).map((item) => Number(item?.points) || 0)));

  if (maxPoint <= 0) {
    return { max: 10, ticks: [0, 2, 4, 6, 8, 10] };
  }

  const axisMax = maxPoint;
  const approxTickStep = axisMax / 5;
  const tickMagnitude = 10 ** Math.floor(Math.log10(Math.max(approxTickStep, 1)));
  const tickCandidates = [1, 2, 5, 10].map((value) => value * tickMagnitude);
  const tickStep =
    tickCandidates.find((value) => value >= approxTickStep) || tickCandidates[tickCandidates.length - 1];

  const ticks = [];
  for (let tick = 0; tick <= axisMax; tick += tickStep) {
    ticks.push(tick);
  }

  if (ticks[ticks.length - 1] !== axisMax) {
    ticks.push(axisMax);
  }

  return { max: axisMax, ticks };
}

function PointsChartTooltip({ active, payload, label }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const value = Number(payload[0]?.value) || 0;

  return (
    <div
      style={{
        backgroundColor: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
        color: "#6B21A8",
        fontSize: 13,
        fontWeight: 600,
        padding: "10px 12px",
      }}
    >
      <div style={{ color: "#6B21A8", marginBottom: 4 }}>{label}</div>
      <div style={{ color: "#6B21A8" }}>{value} Points</div>
    </div>
  );
}

const PointsDashboardPanel = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chartTab, setChartTab] = useState("weekly");
  const [weekOffset, setWeekOffset] = useState(0);
  const [chartMonth, setChartMonth] = useState(() => new Date().getMonth());
  const [chartYear, setChartYear] = useState(() => new Date().getFullYear());
  const [openSection, setOpenSection] = useState(null);

  const clientTimezone = useMemo(() => {
    try {
      const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return z && String(z).trim() ? String(z).trim() : "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const loadersRef = useRef({ startLoading, stopLoading });
  loadersRef.current = { startLoading, stopLoading };

  const load = useCallback(async () => {
    const { startLoading: sl, stopLoading: el } = loadersRef.current;
    sl?.();
    try {
      const [dashOutcome, progOutcome] = await Promise.allSettled([
        api.getPointsDashboard({
          week_offset: weekOffset,
          chart_month: monthIndexToApiMonthKey(chartMonth),
          chart_year: chartYear,
          timezone: clientTimezone,
        }),
        api.getProgress(),
      ]);

      const res =
        dashOutcome.status === "fulfilled" ? dashOutcome.value : null;

      let normalized;
      if (res && isApiSuccess(res) && res.data) {
        normalized = normalizeDashboardPayload(res.data, chartYear, chartMonth);
      } else {
        if (dashOutcome.status === "rejected") {
          logMessage(dashOutcome.reason);
        } else {
          logMessage(`getPointsDashboard non-success: ${res?.message || ""}`, "warn");
        }
        normalized = normalizeDashboardPayload(
          { total_xp: 0, current_level: 1, current_tier: {} },
          chartYear,
          chartMonth
        );
      }

      if (progOutcome.status === "fulfilled") {
        const pr = progOutcome.value;
        if (pr && isApiSuccess(pr) && pr.data) {
          normalized = mergeProgressIntoDashboard(normalized, pr.data);
        }
      }

      setData(normalized);
    } catch (e) {
      logMessage(e);
      setData(
        normalizeDashboardPayload(
          { total_xp: 0, current_level: 1, current_tier: {} },
          chartYear,
          chartMonth
        )
      );
    } finally {
      el?.();
    }
  }, [weekOffset, chartMonth, chartYear, clientTimezone]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const tier = summary.current_tier || {};
  const tierKey = String(tier.key || "bronze").toLowerCase();
  const fallbackTierIconByKey = {
    bronze: imagePaths.bronze,
    silver: imagePaths.silver,
    gold: imagePaths.gold,
    diamond: imagePaths.crown,
  };
  const tierIcon =
    tier.icon || fallbackTierIconByKey[tierKey] || imagePaths.medal || imagePaths.crown;

  const currentLevel = Number(summary.current_level || 1);
  const totalXp = Number(summary.total_xp || 0);
  const { progressPercent, fractionLabel } = getPointsProgressDisplay(
    summary.current_xp,
    summary.xp_required_for_completion
  );

  const chartData = useMemo(() => {
    if (!data?.chart) return emptyWeekly();
    if (chartTab === "weekly") return data.chart.weekly?.series || emptyWeekly();
    if (chartTab === "monthly") return data.chart.monthly?.series || emptyMonthly();
    return data.chart.yearly?.series || emptyYearly();
  }, [data, chartTab]);

  const chartScale = useMemo(() => buildYAxisScale(chartData), [chartData]);

  const periodLabel = useMemo(() => {
    if (!data?.chart) return "";
    if (chartTab === "weekly") return data.chart.weekly?.range_label || "";
    if (chartTab === "monthly") return data.chart.monthly?.month_label || "";
    return String(data.chart.yearly?.year ?? chartYear);
  }, [data, chartTab, chartYear]);

  const monthYearSelected = useMemo(
    () => new Date(chartYear, chartMonth, 1),
    [chartYear, chartMonth]
  );
  const yearOnlySelected = useMemo(() => new Date(chartYear, 0, 1), [chartYear]);

  const bumpMonth = (delta) => {
    const d = new Date(chartYear, chartMonth + delta, 1);
    setChartYear(d.getFullYear());
    setChartMonth(d.getMonth());
  };

  const bumpYear = (delta) => {
    setChartYear((y) => y + delta);
  };

  const ways = data?.ways_to_earn || buildWaysSectionsFromConfig([]);
  const chartAnimationKey = useMemo(
    () =>
      [
        chartTab,
        periodLabel,
        chartScale.max,
        ...chartData.map((item) => `${item.label}:${Number(item.points) || 0}`),
      ].join("|"),
    [chartData, chartScale.max, chartTab, periodLabel]
  );
  const chartLineGradientId = useMemo(
    () => `pointsLineGradient-${chartTab}-${chartYear}-${chartMonth}-${weekOffset}`,
    [chartMonth, chartTab, chartYear, weekOffset]
  );
  const chartFillGradientId = useMemo(
    () => `pointsAreaFill-${chartTab}-${chartYear}-${chartMonth}-${weekOffset}`,
    [chartMonth, chartTab, chartYear, weekOffset]
  );

  return (
    <div
      className="p-4 relative md:p-5 min-h-full pb-10"
      style={{ backgroundColor: PAGE_BG }}
    >
       <div
          className="absolute inset-0 h-48 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/bgImage.webp')" }}
          aria-hidden
        />
    
       
        <div
          className="absolute inset-0 bg-gradient-to-b from-[#3d2660]/92 via-[#552F83]/78 to-[#6b4d96]/45 pointer-events-none"
          aria-hidden
        />
        <div className="relative z-10 px-3 pb-6 md:px-5 md:pb-8">
          <p className="text-[12px] text-white/95 mb-4 drop-shadow-sm">
            <button
              type="button"
              className="cursor-pointer hover:underline text-white/95 bg-transparent border-0 p-0 font-inherit"
              onClick={() => navigate(paths.YOUR_PROGRESS)}
            >
              Your progress
            </button>
            <span className="mx-1.5 opacity-90">&gt;</span>
            <span className="font-medium text-white">Points Dashboard</span>
          </p>

          <div className="bg-white rounded-3xl shadow-lg border-2 border-[#552F83] p-5 md:p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-[#F3F4F6] flex items-center justify-center shrink-0 border border-[#E5E7EB]">
                <img src={tierIcon} alt="" className="w-10 h-10 object-contain" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] tracking-wide text-[#7C7790] uppercase">
                  Current level
                </span>
                <span className="text-2xl md:text-3xl font-bold" style={{ color: PURPLE }}>
                  Level {currentLevel}
                </span>
              </div>
            </div>
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-[#323232] font-medium">Points Progress</span>
                <span className="font-semibold text-[#323232]">{fractionLabel}</span>
              </div>
              <div className="h-3.5 rounded-full overflow-hidden bg-[#F5E6F0]">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: PURPLE,
                  }}
                />
              </div>
              <p className="text-center text-sm text-[#7C7790] mt-3">
                {summary.points_away_text ?? ""}
              </p>
            </div>
          </div>
        </div>
     

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E8E0F5] p-4 flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: PURPLE_SOFT }}
          >
            <FiTrendingUp className="text-xl" style={{ color: PURPLE }} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-lg font-bold leading-tight" style={{ color: PURPLE }}>
              {totalXp} Points
            </span>
            <span className="text-xs text-[#7f7f8d] tracking-wide mt-0.5">Total Earned</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-[#E8E0F5] p-4 flex items-center gap-3">
          <div  style={{ backgroundColor: PURPLE_SOFT }} className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 border border-[#E5E7EB]">
            <img src={tierIcon} alt="" className="w-7 h-7 object-contain" />
          </div>
          <div className="flex flex-col gap-1"  >
            <span className="text-lg font-bold leading-tight" style={{ color: PURPLE }}>
              {tier.name || "Bronze"}
            </span>
            <span className="text-xs text-[#7f7f8d] tracking-wide mt-0.5">Current Tier</span>
          </div>
        </div>
      </div>

      <div className="relative z-0 bg-white rounded-3xl shadow-sm border border-[#E8E0F5] p-4 md:p-5 mb-4 overflow-visible">
        <Tabs
          value={chartTab}
          onValueChange={setChartTab}
          className="w-full"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-2">
            <TabsList className="h-auto flex-wrap justify-start sm:flex-nowrap shrink-0 bg-[#552F8326]">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="yearly">Yearly</TabsTrigger>
            </TabsList>
            <div className="flex items-center justify-between sm:justify-end gap-1 text-[#1E232C] shrink-0 min-w-0 w-full lg:w-auto">
              <button
                type="button"
                className="p-1 rounded-lg  border-0 bg-transparent cursor-pointer shrink-0"
                aria-label="Previous period"
                onClick={() => {
                  if (chartTab === "weekly") setWeekOffset((w) => w - 1);
                  else if (chartTab === "monthly") bumpMonth(-1);
                  else bumpYear(-1);
                }}
              >
                <MdKeyboardArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex min-w-0 flex-1 sm:flex-none justify-center">
                {chartTab === "monthly" ? (
                  <DatePicker
                    selected={monthYearSelected}
                    onChange={(d) => {
                      if (!d) return;
                      setChartYear(d.getFullYear());
                      setChartMonth(d.getMonth());
                    }}
                    showMonthYearPicker
                    dateFormat="MMMM yyyy"
                    customInput={<PeriodPickerButton />}
                    popperClassName="points-dashboard-datepicker-popper"
                    popperProps={{ strategy: "fixed" }}
                    calendarClassName="!rounded-xl !border !border-border !font-sans"
                    wrapperClassName="w-full flex justify-center"
                  /> 
                ) : chartTab === "yearly" ? (
                  <DatePicker
                    selected={yearOnlySelected}
                    onChange={(d) => {
                      if (!d) return;
                      setChartYear(d.getFullYear());
                    }}
                    showYearPicker
                    dateFormat="yyyy"
                    customInput={<PeriodPickerButton />}
                    popperClassName="points-dashboard-datepicker-popper"
                    popperProps={{ strategy: "fixed" }}
                    calendarClassName="!rounded-xl !border !border-border !font-sans"
                    wrapperClassName="w-full flex justify-center"
                  />
                ) : (
                  <span className="text-sm font-medium min-w-0 max-w-[16rem] truncate text-center text-foreground px-2 py-1.5">
                    {periodLabel}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="p-1 rounded-lg hover:bg-[#F3F4F6] border-0 bg-transparent cursor-pointer shrink-0"
                aria-label="Next period"
                onClick={() => {
                  if (chartTab === "weekly") setWeekOffset((w) => w + 1);
                  else if (chartTab === "monthly") bumpMonth(1);
                  else bumpYear(1);
                }}
              >
                <MdKeyboardArrowRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </Tabs>
        <motion.div
          key={chartAnimationKey}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="points-dashboard-chart-wrap w-full mt-2"
          style={{ width: "100%", height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT} minWidth={48}>
            <AreaChart
              data={chartData}
              margin={{ top: 12, right: 12, left: 4, bottom: 8 }}
            >
              <defs>
                <linearGradient id={chartLineGradientId} x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#512B80" />
                  <stop offset="100%" stopColor="#A33B89" />
                </linearGradient>
                <linearGradient id={chartFillGradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(81, 43, 128, 0.25)" />
                <stop offset="100%" stopColor="rgba(255, 255, 255, 0.25)" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ECE5F8" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#7C7790" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#7C7790" }}
                axisLine={false}
                tickLine={false}
                domain={[0, chartScale.max]}
                ticks={chartScale.ticks}
                allowDecimals={false}
                tickFormatter={(value) => value}
                width={40}
              />
              <Tooltip content={<PointsChartTooltip />} />
              <Area
                key={`points-area-${chartAnimationKey}`}
                type="monotone"
                dataKey="points"
                stroke={`url(#${chartLineGradientId})`}
                strokeWidth={2.22}
                fill={`url(#${chartFillGradientId})`}
                dot={{ r: 3, fill: PURPLE, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: PURPLE }}
                isAnimationActive
                animationDuration={700}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      <h2 className="text-base font-bold text-[#2f2f39] mb-3">Ways to earn</h2>
      <div className="space-y-3">
        {ways.map((section) => {
          const open = openSection === section.id;
          return (
            <div
              key={section.id}
              className="bg-white rounded-2xl border border-[#E8E0F5] shadow-sm overflow-hidden"
            >
              <button
                type="button"
                className={`w-full flex items-center gap-3 p-4 text-left border-0 cursor-pointer transition-colors ${
                  open
                    ? "bg-white hover:bg-white"
                    : "bg-transparent hover:bg-[#F9F7FF]"
                }`}
                onClick={() => setOpenSection(open ? null : section.id)}
              >
                <SectionIcon type={section.icon} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-[#323232]">{section.title}</span>
                    <span className="text-xs text-[#7a7391] mt-0.5">{section.subtitle}</span>
                  </div>
                </div>
                <motion.span
                  className="inline-flex shrink-0 text-[#323232]"
                  animate={{ rotate: open ? 180 : 0 }}
                  transition={waysAccordionTransition}
                  aria-hidden
                >
                  <MdKeyboardArrowDown className="w-6 h-6" />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key={`ways-panel-${section.id}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={waysAccordionTransition}
                    className="overflow-hidden border-t border-[#ECE5F8]"
                  >
                    <div className="px-4 pb-4 pt-0">
                      <div className="grid md:grid-cols-2 gap-4 pt-3">
                        <div>
                          <ul className="list-none space-y-2 m-0 p-0 mt-0">
                            {(section.tasks || []).map((t) => (
                              <li
                                key={t.key}
                                className="flex gap-3 items-start text-xs text-[#323232]"
                              >
                                <span className="min-w-0 flex-1 break-words text-left text-[#323232] font-medium leading-snug">
                                  {t.description || TASK_LABEL_BY_KEY[t.key] || t.key}
                                </span>
                                <span
                                  className="font-semibold shrink-0 text-right whitespace-nowrap"
                                  style={{ color: PURPLE }}
                                >
                                  {t.points_label
                                    ? t.points_label
                                    : t.points != null
                                      ? `+${t.points} Points`
                                      : ""}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <div
                            className="rounded-xl p-3 text-sm border border-[#E8E0F5] text-[#552F83]"
                            style={{
                              background:
                                "linear-gradient(273.73deg, #FFFFFF -14.43%, #EEECFF 188.05%)",
                            }}
                          >
                            <span className="text-xs font-semibold uppercase mb-0 flex items-center gap-1.5 text-[#552F83]">
                              <DiamondIcon />
                              Good to know
                            </span>
                            <ul className="list-disc mt-1 pl-4 space-y-0.5 font-normal leading-tight [&_li]:leading-[1.25] marker:text-[#323232] text-[#323232]">
                              {(section.good_to_know || []).map((line, i) => (
                                <li key={i} className="pl-0.5">
                                  {line}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PointsDashboardPanel;
